import { useState, useEffect } from "react";
import { Bot, Mail, Clock, Zap, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";

interface Counterparty {
  id: string;
  name: string;
  type: string;
  email_primary: string | null;
  email_cc: string[] | null;
}

interface DeployAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function DeployAgentDialog({ open, onOpenChange, onSuccess }: DeployAgentDialogProps) {
  const { orgId, familyId } = useTenant();
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    counterparty_id: "",
    primary_email: "",
    cc_emails: "",
    cadence_first: "0",
    cadence_reminder: "3",
    cadence_escalate: "7",
    auto_send: false,
    require_approval: true,
    quiet_hours_start: "20:00",
    quiet_hours_end: "08:00",
    max_attempts: "3",
    custom_template: "",
    escalation_action: "none" as "none" | "cc_manager" | "create_action" | "notify_principal",
    escalation_emails: "",
  });

  useEffect(() => {
    if (open) {
      fetchCounterparties();
    }
  }, [open, orgId, familyId]);

  const fetchCounterparties = async () => {
    try {
      const { data, error } = await supabase
        .from("counterparties")
        .select("*")
        .eq("org_id", orgId)
        .order("name");

      if (error) throw error;
      setCounterparties(data || []);
    } catch (err) {
      console.error("Failed to fetch counterparties:", err);
      toast.error("Failed to fetch counterparties");
    }
  };

  const handleCounterpartyChange = (counterpartyId: string) => {
    const cp = counterparties.find(c => c.id === counterpartyId);
    setFormData({
      ...formData,
      counterparty_id: counterpartyId,
      name: cp ? `${cp.name} Chaser Agent` : "",
      primary_email: cp?.email_primary || "",
      cc_emails: cp?.email_cc?.join(", ") || "",
    });
  };

  const handleDeploy = async () => {
    if (!formData.counterparty_id) {
      toast.error("Please select a fund manager");
      return;
    }
    if (!formData.primary_email) {
      toast.error("Please provide a primary email");
      return;
    }

    try {
      setLoading(true);

      const config = {
        primary_email: formData.primary_email,
        cc_emails: formData.cc_emails.split(",").map(e => e.trim()).filter(Boolean),
        cadence: {
          first: parseInt(formData.cadence_first),
          reminder: parseInt(formData.cadence_reminder),
          escalate: parseInt(formData.cadence_escalate),
        },
        auto_send: formData.auto_send,
        require_approval: formData.require_approval,
        quiet_hours: {
          start: formData.quiet_hours_start,
          end: formData.quiet_hours_end,
        },
        max_attempts: parseInt(formData.max_attempts),
        custom_template: formData.custom_template || null,
        escalation_action: formData.escalation_action,
        escalation_emails: formData.escalation_emails.split(",").map(e => e.trim()).filter(Boolean),
      };

      const { error } = await supabase
        .from("chaser_agents")
        .insert({
          org_id: orgId,
          family_id: familyId,
          counterparty_id: formData.counterparty_id,
          name: formData.name,
          status: "active",
          config,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        });

      if (error) throw error;

      toast.success("Chaser agent deployed successfully!");
      onOpenChange(false);
      onSuccess();
      // Reset form
      setFormData({
        name: "",
        counterparty_id: "",
        primary_email: "",
        cc_emails: "",
        cadence_first: "0",
        cadence_reminder: "3",
        cadence_escalate: "7",
        auto_send: false,
        require_approval: true,
        quiet_hours_start: "20:00",
        quiet_hours_end: "08:00",
        max_attempts: "3",
        custom_template: "",
        escalation_action: "none",
        escalation_emails: "",
      });
    } catch (err) {
      console.error("Failed to deploy agent:", err);
      toast.error("Failed to deploy agent");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Bot className="h-4 w-4" />
          Deploy Agent
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Deploy Chaser Agent
          </DialogTitle>
          <DialogDescription>
            Configure an automated agent to chase missing documents from a fund manager
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Fund Manager Selection */}
          <div className="space-y-2">
            <Label>Fund Manager / Counterparty</Label>
            <Select value={formData.counterparty_id} onValueChange={handleCounterpartyChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select fund manager" />
              </SelectTrigger>
              <SelectContent>
                {counterparties.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">
                    No counterparties found. Add one in Settings → Requirements first.
                  </div>
                ) : (
                  counterparties.map((cp) => (
                    <SelectItem key={cp.id} value={cp.id}>
                      {cp.name} ({cp.type.toUpperCase()})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Agent Name */}
          <div className="space-y-2">
            <Label>Agent Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Fund VII Quarterly Chaser"
            />
          </div>

          <Separator />

          {/* Contact Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium">Contact Information</h3>
            </div>

            <div className="space-y-2">
              <Label>Primary Email *</Label>
              <Input
                type="email"
                value={formData.primary_email}
                onChange={(e) => setFormData({ ...formData, primary_email: e.target.value })}
                placeholder="ir@fundvii.com"
              />
            </div>

            <div className="space-y-2">
              <Label>CC Emails (comma separated)</Label>
              <Input
                value={formData.cc_emails}
                onChange={(e) => setFormData({ ...formData, cc_emails: e.target.value })}
                placeholder="cfo@fundvii.com, admin@fundvii.com"
              />
            </div>
          </div>

          <Separator />

          {/* Automation Rules */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium">Automation Rules</h3>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>First Chase (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.cadence_first}
                  onChange={(e) => setFormData({ ...formData, cadence_first: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Days after due date</p>
              </div>

              <div className="space-y-2">
                <Label>Reminder (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.cadence_reminder}
                  onChange={(e) => setFormData({ ...formData, cadence_reminder: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Days after first</p>
              </div>

              <div className="space-y-2">
                <Label>Escalate (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.cadence_escalate}
                  onChange={(e) => setFormData({ ...formData, cadence_escalate: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Days after reminder</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Maximum Attempts</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={formData.max_attempts}
                onChange={(e) => setFormData({ ...formData, max_attempts: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label>Auto-Send</Label>
                  <Badge variant={formData.auto_send ? "destructive" : "secondary"}>
                    {formData.auto_send ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Send emails automatically without approval
                </p>
              </div>
              <Switch
                checked={formData.auto_send}
                onCheckedChange={(checked) => setFormData({ ...formData, auto_send: checked, require_approval: !checked })}
              />
            </div>

            {formData.auto_send && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                <p className="text-sm text-destructive">
                  Auto-send will send emails without your approval. Use with caution.
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Quiet Hours */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium">Quiet Hours</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={formData.quiet_hours_start}
                  onChange={(e) => setFormData({ ...formData, quiet_hours_start: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={formData.quiet_hours_end}
                  onChange={(e) => setFormData({ ...formData, quiet_hours_end: e.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Agent will not send emails during these hours (counterparty timezone)
            </p>
          </div>

          <Separator />

          {/* Custom Template */}
          <div className="space-y-2">
            <Label>Custom Email Template (optional)</Label>
            <Textarea
              value={formData.custom_template}
              onChange={(e) => setFormData({ ...formData, custom_template: e.target.value })}
              placeholder="Hi {ContactName},&#10;&#10;We're still awaiting the {DocType} for {Period}...&#10;&#10;Available variables: {ContactName}, {FundName}, {DocType}, {Period}, {DueDate}"
              rows={6}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use the default template
            </p>
          </div>

          <Separator />

          {/* Escalation */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium">Escalation</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              What happens when all chase attempts are exhausted without a response?
            </p>

            <div className="space-y-2">
              <Label>Escalation Action</Label>
              <Select
                value={formData.escalation_action}
                onValueChange={(value: "none" | "cc_manager" | "create_action" | "notify_principal") =>
                  setFormData({ ...formData, escalation_action: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No escalation</SelectItem>
                  <SelectItem value="cc_manager">Email manager(s)</SelectItem>
                  <SelectItem value="create_action">Create action item</SelectItem>
                  <SelectItem value="notify_principal">Notify principal + create action</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.escalation_action !== "none" && formData.escalation_action !== "create_action" && (
              <div className="space-y-2">
                <Label>Escalation Email(s)</Label>
                <Input
                  value={formData.escalation_emails}
                  onChange={(e) => setFormData({ ...formData, escalation_emails: e.target.value })}
                  placeholder="manager@example.com, principal@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  Comma-separated email addresses to notify on escalation
                </p>
              </div>
            )}
          </div>

          <Button onClick={handleDeploy} disabled={loading} className="w-full">
            {loading ? "Deploying..." : "Deploy Agent"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
